'use client';

import { useSyncExternalStore } from 'react';
import React from 'react';

export interface OverlayElement<T = void> {
  element: (params: {
    isOpen: boolean;
    close: (value?: T) => void;
    unmount: () => void;
  }) => React.ReactNode;
  overlap?: boolean;
  isOpen: boolean;
  resolve?: (data: T) => void;
}

export function createOverlayStore() {
  const initElements: OverlayElement[] = [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let elements: OverlayElement<any>[] = initElements;
  const subscribers = new Set<() => void>();

  const notify = () => {
    subscribers.forEach((fn) => fn());
  };

  const getSnapshot = () => elements;
  const getServerSnapshot = () => initElements;

  const subscribe = (callback: () => void) => {
    subscribers.add(callback);
    return () => subscribers.delete(callback);
  };

  const open = <T>(options: Omit<OverlayElement<T>, 'resolve' | 'isOpen'>): Promise<T> => {
    return new Promise<T>((resolve) => {
      const element: OverlayElement<T> = {
        ...options,
        isOpen: true,
        resolve,
      };
      elements = options.overlap ? [element, ...elements] : [...elements, element];
      notify();
    });
  };

  const close = <T>(value: T) => {
    const el = elements[0];

    if (el && el.isOpen) {
      el.isOpen = false;

      el.resolve?.(value);
      elements = [...elements];
      notify();
    }
  };

  const unmount = () => {
    if (elements.length > 0) {
      elements.shift();
      elements = [...elements];
      notify();
    }
  };

  const clear = () => {
    elements = [];
    notify();
  };

  const OverlaySpace = () => {
    const elements = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
    const el = elements[0];

    if (!el) {
      return null;
    }

    return el.element({ isOpen: el.isOpen, close, unmount });
  };

  return { open, OverlaySpace, clear };
}
